import { Injectable, Inject, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthResponse, AuthUserModel } from './auth-user.model';
import { ROUTE_SIGN_IN } from '../_common/constants';
import { ToastrService } from 'ngx-toastr';
import jwtDecode from 'jwt-decode';
import { RoleEnum } from '../administration/users/user-role.enum';
import { BaseHttpResponse } from '../_common/models/core.model';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from '../_common/services/translation.service';
import { BrandService } from '../administration/brands/brand.service';
import { ConfigService } from '../_common/services/config.service';
import { ImageService } from '../_common/services/image.service';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-common';
import { PermissionEnum } from '../administration/users/user-permission.enum';
import { environment } from 'src/environments/environment';

export function appInitializer(
    authService: AuthService,
    translationService: TranslationService,
    brandService: BrandService,
    configService: ConfigService
  ) {
  return () => {
    return Promise.all([
      translationService.getLanguageList(),
      configService.getSettings(),
      authService.authenticate(),
      brandService.getBrandList(),
    ]).then(() => {
    }).catch(error => {
      console.error('Initialization failed', error);
    });
  };
}

@Injectable({
  providedIn: 'root'
})

export class AuthService {

  private authUserSubject: BehaviorSubject<AuthUserModel>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private toastr: ToastrService,
    private translateService: TranslateService,
    @Optional() private msalService: MsalService,
    @Inject('API_URL') private apiUrl: string,
  ) {
    this.authUserSubject = new BehaviorSubject<AuthUserModel>(null);
  }

  public get authenticatedUser(): AuthUserModel {
    return this.authUserSubject.value;
  }

  public hasRole(role: RoleEnum): boolean {
    return this.authUserSubject.value.roles.includes(role);
  }

  public hasPermission(permission: PermissionEnum): boolean {
    return this.authUserSubject.value.permissions.includes(permission);
  }

  authenticate() {
    if (environment.authMode === "Entra"){
      this.setMsalAccount();
    }

    return new Promise<void>((resolve, reject) => {
      this.http.get<AuthResponse>(this.apiUrl + 'auth/authenticate')
        .subscribe({
          next: (response) => {
            if (response.model) {
              let jwt: any = jwtDecode(response.model.token);
              response.model.permissions = jwt.permissions.split(',').map((value) => PermissionEnum[value]);
              //response.model.roles = jwt.roles.split(',').map((value) => RoleEnum[value]);
              this.authUserSubject.next(response.model);
              if (response.model.language) {
                this.translateService.use(response.model.language);
              }
            }
            resolve();
          },
          error: (err) => reject(err)
        });
    });
  }

  signIn(username: string, password: string) {
    return this.http.post<AuthResponse>(this.apiUrl + 'auth/sign-in', { username, password })
      .pipe(map(response => {
        if (response.model) {
          let jwt: any = jwtDecode(response.model.token);
          response.model.id = Number(jwt.id);
          response.model.roles = jwt.roles.split(',').map((value) => RoleEnum[value]);
          this.authUserSubject.next(response.model);
          if (response.model.language) {
            this.translateService.use(response.model.language);
          }
          this.startRefreshTokenTimer();
        }
        return response;
      }));
  }

  signOut() {
    this.http.post<any>(this.apiUrl + 'auth/sign-out', {}).subscribe();
    this.stopRefreshTokenTimer();
    this.authUserSubject.next(null);
    this.router.navigateByUrl(ROUTE_SIGN_IN, { state: { isFromRedirect: true } });
  }

  refreshToken() {
    return this.http.post<AuthResponse>(this.apiUrl + 'auth/refresh-token', {})
      .pipe(map((response) => {
        if (response.model) {
          let jwt: any = jwtDecode(response.model.token);
          response.model.id = Number(jwt.id);
          response.model.roles = jwt.roles.split(',').map((value) => RoleEnum[value]);
          this.authUserSubject.next(response.model);
          if (response.model.language) {
            this.translateService.use(response.model.language);
          }
          this.startRefreshTokenTimer();
        }
        return response;
      }));
  }

  public updateUser(user: AuthUserModel): Observable<boolean> {
    let body = user;
    return this.http.post<BaseHttpResponse<boolean>>(this.apiUrl + 'auth/update-user', body)
      .pipe(
        map(data => {

          if (data.model) {
            this.toastr.success(this.translateService.instant('auth.update-user.success'));
          }

          if (data.error.message) {
            this.toastr.error(data.error.message);
          }

          return data.model;
        })
      );
  }

  private refreshTokenTimeout: NodeJS.Timeout;

  private startRefreshTokenTimer() {
    const jwtToken = JSON.parse(atob(this.authenticatedUser.token.split('.')[1]));

    // set a timeout to refresh the token a 15 seconds before it expires
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - (15 * 1000);
    this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  private setMsalAccount(){
    this.msalService.instance.handleRedirectPromise().then((result: AuthenticationResult | null) => {
      if (result != null && result.account != null) {
        this.msalService.instance.setActiveAccount(result.account);
      }
    });

    if (!this.msalService.instance.getAllAccounts()) {
      this.msalService.loginRedirect();
    }
  }
}
